import React from 'react';
import { Card, CardBody, Table } from 'reactstrap';
import TenderNotClosedAlert from '../Components/TenderNotClosedAlert';
import useTenderEvaluationAttendance from '../../../../hooks/useTenderEvaluationAttendance';
import {
  ATTENDANCE_CONFIRMATION_COLUMNS,
  canAccessEvaluationOperationalData,
  displayValue,
  getEvaluationOperationalLockReason,
  getInvitationEvaluationStatusBadgeConfig,
} from '../../../../utils/tenderEvaluationHelpers';
import ActionButton from '../../../../../../Components/Common/ActionButton';
import { EmptyState } from '../../../../../../Components/Common/ListStates';
import { SkeletonTable } from '../../../../../../Components/Common/Skeleton';
import StatusBadge from '../../../../../../Components/Common/StatusBadge';
import { printAttendancePdf } from '../../../../utils/attendanceDocument';

const Attendance = ({ procurement, procId, isActive = true }) => {
  const lockReason = getEvaluationOperationalLockReason(procurement);
  const canAccessData = canAccessEvaluationOperationalData(procurement);
  const procurementId = procurement?.id || procId;
  const { items, isLoading } = useTenderEvaluationAttendance(procurementId, {
    enabled: canAccessData && Boolean(procurementId),
    isActive,
  });

  const renderCellValue = (column, row, index) => {
    if (column.key === 'no') return displayValue(index + 1);
    if (column.key === 'status') {
      const statusConfig = getInvitationEvaluationStatusBadgeConfig(
        row.invitationEvaluationStatus,
        row.status
      );
      return (
        <StatusBadge tone={statusConfig.tone} label={statusConfig.label} />
      );
    }
    return displayValue(row[column.key]);
  };

  return (
    <Card className='border shadow-md mb-4'>
      <CardBody>
        <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
          <div>
            <h5 className='fw-bold text-dark mb-1'>Attendance Confirmation</h5>
            <p className='text-muted small mb-0'>
              Daftar konfirmasi kehadiran peserta tender evaluation.
            </p>
          </div>
          <ActionButton
            variant='secondary'
            icon='ri-download-2-line'
            className='flex-shrink-0'
            disabled={!canAccessData || !items.length}
            onClick={() => printAttendancePdf({ procurement, items })}
          >
            Download to Document
          </ActionButton>
        </div>

        <hr className='mt-0 mb-3' />

        {!canAccessData ? (
          <TenderNotClosedAlert
            featureLabel='Attendance Confirmation'
            reason={lockReason}
          />
        ) : isLoading ? (
          <SkeletonTable
            rows={4}
            columns={ATTENDANCE_CONFIRMATION_COLUMNS.length}
          />
        ) : !items.length ? (
          <EmptyState
            compact
            icon='ri-user-follow-line'
            title='No attendance confirmations yet'
            emptyHint='Participants have not confirmed their attendance for this tender evaluation yet.'
          />
        ) : (
          <div className='table-responsive'>
            <Table
              className='align-middle table-nowrap mb-0'
              aria-label='Attendance confirmation'
            >
              <thead className='table-light'>
                <tr>
                  {ATTENDANCE_CONFIRMATION_COLUMNS.map(column => (
                    <th key={column.key} className='fs-13'>
                      {column.header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row, index) => (
                  <tr key={row.id ?? index}>
                    {ATTENDANCE_CONFIRMATION_COLUMNS.map(column => (
                      <td key={column.key} className='text-dark fs-13'>
                        {renderCellValue(column, row, index)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default Attendance;
